import React, { useState } from 'react';
import { useUserSettings } from '../../contexts/UserSettingsContext';
import { isFulfillmentFlowBetaOn } from '../../services/userSettingsAPI';
import ChildConnectorElbow from '../shared/ChildConnectorElbow';
import './ConfigureAccount.css';

interface SettingsToggleProps {
  readonly id: string;
  readonly label: string;
  readonly enabled: boolean;
  readonly disabled: boolean;
  readonly onChange: (enabled: boolean) => void;
}

function SettingsToggle({ id, label, enabled, disabled, onChange }: SettingsToggleProps): React.ReactElement {
  const labelId = `${id}-label`;
  return (
    <button
      type="button"
      className={`configure-row${enabled ? ' configure-row-on' : ''}`}
      role="switch"
      aria-checked={enabled}
      aria-labelledby={labelId}
      disabled={disabled}
      onClick={() => onChange(!enabled)}
    >
      <span className="configure-label" id={labelId}>
        {label}
      </span>
      <span className="configure-toggle" aria-hidden="true">
        <span className="configure-toggle-state">{enabled ? 'ON' : 'OFF'}</span>
        <span className="configure-toggle-track">
          <span className="configure-toggle-knob" />
        </span>
      </span>
    </button>
  );
}

function ConfigureAccount(): React.ReactElement {
  const {
    settings,
    setColorMode,
    setCameraOpensOrderDetails,
    setCollapseLocatedBundles,
    setPhotographPackingItems,
    setPrintScanOrderLabel,
    setScanSuggestBoxSize,
    setFulfillmentFlowBeta,
  } = useUserSettings();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleToggle = async (action: () => Promise<void>): Promise<void> => {
    setError('');
    setSaving(true);
    try {
      await action();
    } catch (err: unknown) {
      console.error('Failed to save setting:', err);
      setError('Could not save setting. Try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="configure-page">
      <div className="configure-list">
        <SettingsToggle
          id="fulfillment-flow-beta"
          label="Beta fulfillment flow"
          enabled={isFulfillmentFlowBetaOn(settings)}
          disabled={saving}
          onChange={(enabled) => {
            void handleToggle(() => setFulfillmentFlowBeta(enabled));
          }}
        />
        <div className="configure-children" role="group" aria-label="Beta fulfillment flow steps">
          <div className="configure-child">
            <ChildConnectorElbow className="configure-connector" />
            <SettingsToggle
              id="print-scan-order-label"
              label="Print and scan order label"
              enabled={settings.printScanOrderLabel}
              disabled={saving}
              onChange={(enabled) => {
                void handleToggle(() => setPrintScanOrderLabel(enabled));
              }}
            />
          </div>
          <div className="configure-child">
            <ChildConnectorElbow className="configure-connector" />
            <SettingsToggle
              id="photograph-packing-items"
              label="Take packing photo"
              enabled={settings.photographPackingItems}
              disabled={saving}
              onChange={(enabled) => {
                void handleToggle(() => setPhotographPackingItems(enabled));
              }}
            />
          </div>
          <div className="configure-child">
            <ChildConnectorElbow className="configure-connector" />
            <SettingsToggle
              id="scan-suggest-box-size"
              label="Scan and suggest box size"
              enabled={settings.scanSuggestBoxSize}
              disabled={saving}
              onChange={(enabled) => {
                void handleToggle(() => setScanSuggestBoxSize(enabled));
              }}
            />
          </div>
        </div>
        <SettingsToggle
          id="color-mode"
          label="Color Mode"
          enabled={settings.colorMode}
          disabled={saving}
          onChange={(enabled) => {
            void handleToggle(() => setColorMode(enabled));
          }}
        />
        <SettingsToggle
          id="open-camera-order-details"
          label="Open camera in Order Details"
          enabled={settings.cameraOpensOrderDetails}
          disabled={saving}
          onChange={(enabled) => {
            void handleToggle(() => setCameraOpensOrderDetails(enabled));
          }}
        />
        <SettingsToggle
          id="collapse-located-bundles"
          label="Collapse bundles with a shelf location"
          enabled={settings.collapseLocatedBundles}
          disabled={saving}
          onChange={(enabled) => {
            void handleToggle(() => setCollapseLocatedBundles(enabled));
          }}
        />
      </div>
      {error ? <p className="configure-error">{error}</p> : null}
    </div>
  );
}

export default ConfigureAccount;
